/**
 * The read client. Every method is a GET; there is no post, put or delete in
 * this file and there never will be one.
 */

import type { MeBadge, MeGrants, MeProgress, WidgetConfig } from "./contract";
import type { Session } from "./session";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function read<T>(session: Session, path: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetchRead(session, path, signal);
  } catch (cause) {
    // A network failure surfaces as "Failed to fetch", which is a browser's
    // words rather than anybody's. What a player needs to know is that it was
    // not their doing and that waiting is the whole remedy.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "This window could not reach ActiveKit. It will try again.");
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      response.status === 401
        ? "This session has expired. Reopen the window to continue."
        : "This window could not load right now. Please try again shortly.",
    );
  }

  return (await response.json()) as T;
}

function fetchRead(session: Session, path: string, signal?: AbortSignal): Promise<Response> {
  return fetch(new URL(path, session.apiBaseUrl), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${session.token}`,
      Accept: "application/json",
    },
    // No credentials mode: the platform's cross origin policy does not claim
    // one, and claiming it here would fail the preflight rather than work.
    credentials: "omit",
    signal,
  });
}

export interface PlayClient {
  widgetConfig(signal?: AbortSignal): Promise<WidgetConfig>;
  progress(signal?: AbortSignal): Promise<MeProgress>;
  grants(signal?: AbortSignal): Promise<MeGrants>;
  badge(signal?: AbortSignal): Promise<MeBadge>;
}

export function createClient(session: Session): PlayClient {
  return {
    widgetConfig: (signal) => read<WidgetConfig>(session, "/v1/me/widget-config", signal),
    progress: (signal) => read<MeProgress>(session, "/v1/me/progress", signal),
    grants: (signal) => read<MeGrants>(session, "/v1/me/grants", signal),
    badge: (signal) => read<MeBadge>(session, "/v1/me/badge", signal),
  };
}
